import fetch from "isomorphic-fetch";
import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  HttpLink,
  InMemoryCache,
  InMemoryCacheConfig
} from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { FieldPolicy, FieldReadFunction } from "@apollo/client/cache";
import { AppConfig } from "@ct-react/core";
import { Locale } from "@ct-react/locale";

const anyArticleFieldPolicies: { [fieldName: string]: FieldPolicy | FieldReadFunction } = ({
  features: {
    merge: (existing, incoming) => ({ ...existing, ...incoming })
  },
  address: {
    merge: (existing, incoming) => ({ ...existing, ...incoming })
  },
  seoData: {
    merge: (existing, incoming) => ({ ...existing, ...incoming })
  }
});

const defaultCacheOptions: InMemoryCacheConfig = {
  dataIdFromObject: res => {
    if (res.__typename === "Cart") return `Cart:${res.cartId}`;
    return defaultDataIdFromObject(res)
  },
  possibleTypes: {
    AnyArticle: [
      "RentalAccommodationArticle",
      "SeasonalAccommodationArticle",
      "AnnualAccommodationArticle",
      "SaleAccommodationArticle"
    ]
  },
  typePolicies: {
    RentalAccommodationArticle: {
      fields: {
        ...anyArticleFieldPolicies,
        costDescriptions: {
          merge: (existing, incoming) => ({ ...existing, ...incoming })
        }
      }
    },
    SeasonalAccommodationArticle: {
      fields: { ...anyArticleFieldPolicies }
    },
    AnnualAccommodationArticle: {
      fields: { ...anyArticleFieldPolicies }
    },
    SaleAccommodationArticle: {
      fields: { ...anyArticleFieldPolicies }
    }
  }
};

const consumerMiddleware = (channelId: string, locale?: Locale) => new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      "GQL-Consumer-Channel": channelId,
      ...(!!locale) && { "GQL-Consumer-Lang": locale.basename }
    }
  }));
  return forward(operation);
});

export const buildApolloClient = (ctx: AppConfig, ssrMode: boolean = false, locale?: Locale, restoreData?: any) => {
  const cache = new InMemoryCache(defaultCacheOptions);
  (!!restoreData) && cache.restore(restoreData);

  return new ApolloClient({
    ssrMode,
    devtools: { enabled: !ssrMode && ctx.env === "development" },
    link: ApolloLink.from([
      new ErrorLink(e => console.log("content error", e)),
      consumerMiddleware(ctx.gqlChannelId, locale),
      new HttpLink({ uri: ctx.gqlClientUrl, fetch })
    ]),
    cache: cache
  });
}

export { type Location, type BasicPrice, resumeLocation } from "./models/common";
export { type Asset, type Image, type ExternalMedia, type Document, type AnyMedia, isImageMedia } from "./models/media";
export { type RentalAccommodationFeatures, type Classification, type RentalPrice, type RentalAccommodationArticleDetails } from "./models/articles/rental";
export { type SeasonalAccommodationFeatures, type SeasonalPrice, type SeasonalAccommodationArticleDetails } from "./models/articles/seasonal";
export { type AnnualAccommodationFeatures, type AnnualPrice, type AnnualAccommodationArticleDetails } from "./models/articles/annual";
export { type SaleAccommodationFeatures, type SaleAccommodationArticleDetails } from "./models/articles/sale";
export { type HeatingSystem, type Orientation, type AnyArticleDetails, isRentalArticle, isSaleArticle } from "./models/articles/index";
export * from "./models/search/filters";
export * from "./models/search/sort";
export * from "./models/search/result";
export { AgeCategory, CartOptionType, type BenefitCartOption, type TouristTaxCartOption, type AnyCartOption } from "./models/cart/options";
export { type CartPrice, type CartItemPrice, type DownPayment, type RentalParticipant, type RentalAccommodationCartItem, type AnyCartItem, isRentalAccommodationCartItem, isCartItemPrice } from "./models/cart/item";
export { type CustomerInput, type CartItemOptionInput, type CartItemTaxOptionInput } from "./models/cart/mutation";
export { type User, type UserInput } from "./models/user";
export { type MailInput, type MailContact, type CustomerContact } from "./models/mail";
export { type CmsPageContent, extractCmsContent } from "./models/cms";
export { type CustomerReview, type ReviewsRates, type ReviewsData } from "./models/reviews";

export default buildApolloClient;
